/**
 * Écoles & éducation — annuaire MENJ via data.education.gouv.fr.
 *
 * Source : `fr-en-annuaire-education` (OpenDataSoft v2.1). CORS ✅, anonyme,
 * pas de quota. Returns every school (maternelle → lycée + supérieur) within
 * a tier-adapted radius around the fiche's geocoded point.
 *
 * Radius — piloté par le tier de la commune (`localityTier.ts`) :
 *   rural       15 km  — lycée souvent à > 10 km en commune < 2 000 hab
 *   petite      10 km  — 2–10 k hab
 *   moyenne      5 km  — 10–50 k hab
 *   grande       3 km  — 50–200 k hab
 *   métropole    2 km  — > 200 k hab + arrondissements Paris/Lyon/Marseille
 *
 * En urbain dense l'API capée à 100 résultats renvoie les 100 plus proches
 * grâce au `order_by=distance` ci-dessous — on couvre donc systématiquement
 * tout le maillage de quartier (mat+élém+collège ≲ 100 dans le rayon < 2 km).
 *
 * We skip the MENJ "résultats au bac / DNB" dataset for this iteration —
 * joining it to the annuaire by `identifiant_de_l_etablissement` adds a
 * second fetch per fiche and a tricky cross-year alignment (DEPP publishes
 * per rentrée while the annuaire is snapshot-daily). Reserved for a future
 * V2 of this block once we see real demand from users.
 */

import { useMemo } from "react";
import { coordKey, haversineMeters, useEnvBlock } from "./shared";
import { useCommuneInfo, type LocalityTier } from "./localityTier";
import type { GeocodeResult } from "../types";

// Radius per locality tier, in meters. Exported so a future map-enabled
// rendering can read the same value without duplicating the table.
export const ECOLES_RADIUS_BY_TIER: Record<LocalityTier, number> = {
  rural: 15_000,
  petite: 10_000,
  moyenne: 5_000,
  grande: 3_000,
  metropole: 2_000,
};

// How long a cached écoles block stays valid. The annuaire updates on a
// rolling basis (daily ingests), but school openings/closings on the scale
// of < 1 year per building — a 30-day stale window is plenty.
const ECOLES_TTL_DAYS = 30;

const ANNUAIRE_URL =
  "https://data.education.gouv.fr/api/explore/v2.1/catalog/datasets/fr-en-annuaire-education/records";

export type EcoleKind =
  | "maternelle"
  | "elementaire"
  | "primaire" // mixte mater+élém in the same établissement
  | "college"
  | "lycee"
  | "autre"; // supérieur, spécialisé, IME…

export type EcoleStatus = "public" | "prive" | "unknown";

export type EcoleItem = {
  id: string;
  name: string;
  kind: EcoleKind;
  status: EcoleStatus;
  address: string;
  postcode: string;
  city: string;
  lat: number;
  lng: number;
  distance_m: number;
};

export type EcolesData = {
  center: { lat: number; lng: number };
  radius_m: number;
  tier: LocalityTier;
  // Source snapshot timestamp (ISO). Not the cache timestamp — that lives on
  // the wrapper EnvBlockDoc.
  fetchedAt: string;
  items: EcoleItem[];
};

// ─── Raw API row shape (only the fields we select) ──────────────────────

type ApiRow = {
  identifiant_de_l_etablissement?: string | null;
  nom_etablissement?: string | null;
  type_etablissement?: string | null;
  statut_public_prive?: string | null;
  adresse_1?: string | null;
  code_postal?: string | null;
  nom_commune?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

// ─── Fetch ──────────────────────────────────────────────────────────────

export async function fetchEcoles(
  lat: number,
  lng: number,
  radiusM: number,
  tier: LocalityTier,
): Promise<EcolesData> {
  // ODSQL `within_distance(<field>, geom'POINT(lng lat)', <radius>m)` is the
  // geo filter. Unlike v1's `geofilter.distance`, v2.1 requires the POINT
  // literal to be lng-lat (not lat-lng) — WKT convention.
  const point = `geom'POINT(${lng} ${lat})'`;
  const where = `within_distance(position, ${point}, ${radiusM}m)`;
  // `order_by=distance(...) ASC` — critical at large radii : OpenDataSoft
  // v2.1 caps any single response at 100 rows, and without an explicit sort
  // urban fiches (Paris, Lyon) would get an arbitrary slice instead of the
  // 100 nearest schools. With the sort, we always retain the useful head.
  const params = new URLSearchParams({
    where,
    limit: "100",
    order_by: `distance(position, ${point}) ASC`,
    select: [
      "identifiant_de_l_etablissement",
      "nom_etablissement",
      "type_etablissement",
      "statut_public_prive",
      "adresse_1",
      "code_postal",
      "nom_commune",
      "latitude",
      "longitude",
    ].join(","),
  });

  const res = await fetch(`${ANNUAIRE_URL}?${params.toString()}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`annuaire-education HTTP ${res.status}`);
  }
  const body = (await res.json()) as { results?: ApiRow[] };
  const rows = Array.isArray(body.results) ? body.results : [];

  const items: EcoleItem[] = [];
  for (const r of rows) {
    if (
      typeof r.latitude !== "number" ||
      typeof r.longitude !== "number" ||
      !r.identifiant_de_l_etablissement ||
      !r.nom_etablissement
    ) {
      continue;
    }
    // The API already pre-filtered by within_distance, but we re-compute
    // locally to (a) get an exact distance for display and (b) sort by it.
    const d = haversineMeters(lat, lng, r.latitude, r.longitude);
    items.push({
      id: r.identifiant_de_l_etablissement,
      name: r.nom_etablissement,
      kind: classifyKind(r.type_etablissement),
      status: classifyStatus(r.statut_public_prive),
      address: (r.adresse_1 ?? "").trim(),
      postcode: r.code_postal ?? "",
      city: (r.nom_commune ?? "").trim(),
      lat: r.latitude,
      lng: r.longitude,
      distance_m: Math.round(d),
    });
  }

  items.sort((a, b) => a.distance_m - b.distance_m);

  return {
    center: { lat, lng },
    radius_m: radiusM,
    tier,
    fetchedAt: new Date().toISOString(),
    items,
  };
}

// ─── Normalisation ──────────────────────────────────────────────────────

function classifyKind(raw: string | null | undefined): EcoleKind {
  if (!raw) return "autre";
  const s = raw.toLowerCase();
  // Order matters — "école élémentaire" contains "école" so we test the
  // more specific labels first.
  if (s.includes("maternelle") && s.includes("él")) return "primaire"; // "école maternelle et élémentaire"
  if (s.includes("primaire")) return "primaire";
  if (s.includes("maternelle")) return "maternelle";
  if (s.includes("élémentaire") || s.includes("elementaire")) return "elementaire";
  if (s.includes("collège") || s.includes("college")) return "college";
  if (s.includes("lycée") || s.includes("lycee")) return "lycee";
  // Bare "Ecole" (no qualifier) is the common Paris/IDF label for a combined
  // mater+élém établissement. Map to "primaire" — the neutral case — rather
  // than dropping to "autre" where the row would disappear from the grouped
  // list. Tested on Paris : ~60 % of primary schools carry this label.
  if (s === "ecole" || s === "école") return "primaire";
  return "autre";
}

function classifyStatus(raw: string | null | undefined): EcoleStatus {
  if (!raw) return "unknown";
  const s = raw.toLowerCase();
  if (s.startsWith("public")) return "public";
  if (s.startsWith("privé") || s.startsWith("prive")) return "prive";
  return "unknown";
}

// ─── Hook ───────────────────────────────────────────────────────────────

/**
 * `useEcoles` — cache-first loader bound to a fiche + geocoded point.
 * Inputs are keyed on the 3-decimal-rounded (lat,lng) AND the commune's
 * locality tier, so :
 *   - two fiches in the same ~100 m square + same tier share cache logic
 *   - a commune that gets re-classified (e.g. population crosses 50k) is
 *     automatically refetched at the next access
 * The hook waits for `useCommuneInfo` to resolve before even scheduling
 * the Firestore read — avoids a wasted round-trip at the old radius.
 */
export function useEcoles(
  ficheId: string | null,
  geocode: GeocodeResult | null,
) {
  const commune = useCommuneInfo(geocode?.insee ?? null);

  const opts = useMemo(() => {
    if (!ficheId || !geocode || !commune) return null;
    const radius = ECOLES_RADIUS_BY_TIER[commune.tier];
    return {
      ficheId,
      blockId: "ecoles" as const,
      inputsKey: `v2|${coordKey(geocode.lat, geocode.lng)}|r${radius}|t${commune.tier}`,
      ttlDays: ECOLES_TTL_DAYS,
      fetcher: () => fetchEcoles(geocode.lat, geocode.lng, radius, commune.tier),
    };
  }, [ficheId, geocode, commune]);

  return useEnvBlock<EcolesData>(opts);
}
